import { mapGetters, mapActions } from 'vuex';
import periodMixin from './period.js';

export default {
    mixins: [periodMixin],
    props: {
        domain: {
            type: Object,
            required: true,
            default: () => {}
        },
        productType: {
            type: String,
            required: true
        }
    },
    computed: {
        ...mapGetters({
            getOrderLineItem: 'order/service',
            getProductByName: 'products/productByName',
            servers: 'servers/availableServers',
            serverUsed: 'order/serverUsed',
            addonsOrdered: 'order/addons',
        }),
        /**
         * Get the service from the order.
         *
         * @return {object}
         */
        orderLineItem() {
            if (!this.productType || !this.domain || !this.domain.domain) {
                return;
            }

            return this.getOrderLineItem({
                type: this.productType,
                domain: this.domain.domain
            });
        },
        /**
         * Get the product by service name from the order.
         *
         * @return {object}
         */
        serviceProduct() {
            if (!this.orderLineItem) {
                return;
            }

            return this.getProductByName(this.orderLineItem.name);
        },
        /**
         * Get the product prices by type.
         *
         * @return {array}
         */
        productPrices() {
            if (!this.serviceProduct) {
                return;
            }

            return this.serviceProduct.prices.filter(productPrice => productPrice.type == this.orderLineItem.status);
        },
        /**
         * Get the current period to display in the select.
         *
         * @return {number}
         */
        currentPeriod() {
            if (!this.orderLineItem) {
                return this.defaultPeriod;
            }

            return this.orderLineItem.period;
        },
        /**
         * Get the current selected server.
         *
         * @return {number}
         */
        currentServer() {
            if (!this.orderLineItem) {
                return;
            }

            return this.orderLineItem.server;
        },
        /**
         * Get how much the selected server is used.
         *
         * @return {number}
         */
        currentServerUsed() {
            if (!this.currentServer) {
                return 0;
            }

            return this.serverUsed(this.currentServer);
        },
        /**
         * Get the available servers.
         *
         * @return {array}
         */
        availableServers() {
            if (!this.servers) {
                return [];
            }

            return this.servers.filter(
                server =>
                    !this.serverUsed(server.id) ||
                    server.available_websites > this.serverUsed(server.id) ||
                    server.id == this.currentServer
            );
        },
        /**
         * Set the server options.
         *
         * @return {array}
         */
        serverOptions() {
            let servers = this.availableServers.map(server => {
                let serverName = server.hostname;

                if (server.name) {
                    serverName = server.name + ' (' + server.hostname + ')';
                }

                return {
                    key: server.id,
                    value: serverName
                };
            });

            if (servers.length) {
                servers.unshift({
                    key: 0,
                    value: trans('hosting.shared-server')
                });
                servers.unshift({
                    // trigger a NaN, so the value becomes null
                    key: '',
                    value: trans('order.select-server')
                });
            }

            return servers;
        },
        /**
         * Check if there are server options.
         *
         * @return {boolean}
         */
        hasServerOptions() {
            return !this.isForwardingService &&
                !this.isVpsService &&
                this.productType == 'hosting' &&
                this.serverOptions.length;
        },
        /**
         * Check if the service is forwarding.
         *
         * @return {boolean}
         */
        isForwardingService() {
            return (
                this.productType == 'hosting' &&
                this.serviceProduct.descriptors.small['product-spec.hosting.services.forwarding'] == true
            );
        },
        /**
         * Check if the service is a vps.
         *
         * @return {boolean}
         */
        isVpsService() {
            return (
                this.productType == 'vps' &&
                this.serviceProduct.descriptors.small['product-spec.hosting.services.vps'] == true
            );
        },
        /**
         * Check if the domain has the automatic private WHOIS option.
         *
         * @return {boolean}
         */
        hasPrivateWhoisOption() {
            return (
                this.productType == 'domains' &&
                this.serviceProduct.descriptors.small['product-spec.domain.allow_whois_private'] == 'yes'
            );
        },
        /**
         * Check if we want to show the support text for private WHOIS.
         *
         * @return {boolean}
         */
        hasPrivateWhoisSupportOption() {
            return (
                this.productType == 'domains' &&
                this.serviceProduct.descriptors.small['product-spec.domain.allow_whois_private'] == 'support'
            );
        }
    },
    methods: {
        ...mapActions({
            removeService: 'order/removeService',
            setServicePeriod: 'order/setServicePeriod',
            setServicePrice: 'order/setServicePrice',
            setServiceServer: 'order/setServiceServer',
            setServiceForwardingUrl: 'order/setForwardingUrl',
            setServiceVpsTemplate: 'order/setVpsTemplate',
            setServiceVpsPassword: 'order/setVpsPassword',
            setServiceVpsSubdomain: 'order/setVpsSubdomain',
        }),
        /**
         * Set the service period.
         *
         * @param {string} period
         */
        setPeriod(period) {
            this.setServicePeriod({
                type: this.productType,
                domain: this.domain.domain,
                period: parseInt(period)
            });
            this.setServicePrice({
                type: this.productType,
                domain: this.domain.domain,
                price: this.productPrice,
                grossPrice: this.grossProductPrice
            });
            // This is some special code to update addons.
            let addon = this.addonsOrdered({domain: this.domain.domain});
            if (this.productType === 'domains' && addon) {
                const price = addon.prices.find(addonPrice => addonPrice.period === parseInt(period));
                this.setServicePeriod({
                    type: 'addons',
                    domain: this.domain.domain,
                    period: parseInt(period)
                });
                this.setServicePrice({
                    type: 'addons',
                    domain: this.domain.domain,
                    price: price.promotion_price || price.regular_price,
                    grossPrice: price.regular_price
                });
            }
        },
        /**
         * Set the service server.
         *
         * @param {string} server
         */
        setServer(server) {
            this.setServiceServer({
                type: this.productType,
                domain: this.domain.domain,
                server: parseInt(server)
            });
        },
        /**
         * Set the service forwaring url.
         *
         * @param {string} forwardingUrl
         */
        setForwardingUrl(forwardingUrl) {
            this.setServiceForwardingUrl({
                type: this.productType,
                domain: this.domain.domain,
                forwardingUrl: forwardingUrl
            });
        },

        setVpsTemplate(vpsTemplateSlug) {
            this.setServiceVpsTemplate({
                type: this.productType,
                domain: this.domain.domain,
                vpsTemplateSlug: vpsTemplateSlug
            });
        },

        setVpsPassword(vpsPassword) {
            this.setServiceVpsPassword({
                type: this.productType,
                domain: this.domain.domain,
                vpsPassword: vpsPassword
            });
        },

        setVpsSubdomain(vpsSubdomain) {
            this.setServiceVpsSubdomain({
                type: this.productType,
                domain: this.domain.domain,
                vpsSubdomain: vpsSubdomain
            });
        },
    }
};
